import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { inr } from "./shared";
import { toast } from "sonner";
import { Plus, Trash2, Send, Award, Loader2 } from "lucide-react";
import PhotoAttachments from "./PhotoAttachments";

export default function WorkflowSheet({ requirement, open, onClose, onChanged }) {
  const rid = requirement?.requirement_id;
  const [tab, setTab] = useState("inspection");

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display">{requirement?.ref} — {requirement?.title}</SheetTitle>
        </SheetHeader>
        <div className="text-xs text-muted-foreground mb-4">{requirement?.category} · Status: <b>{requirement?.status}</b></div>
        {rid && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="inspection" data-testid="wf-tab-inspection">Inspection</TabsTrigger>
              <TabsTrigger value="boq" data-testid="wf-tab-boq">BOQ</TabsTrigger>
              <TabsTrigger value="rfq" data-testid="wf-tab-rfq">RFQ & Quotes</TabsTrigger>
            </TabsList>
            <TabsContent value="inspection"><Inspection rid={rid} onChanged={onChanged} /></TabsContent>
            <TabsContent value="boq"><BOQ rid={rid} onChanged={onChanged} /></TabsContent>
            <TabsContent value="rfq"><RFQ requirement={requirement} onChanged={onChanged} /></TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SocietyQuote({ rfq }) {
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (open && form === null) api.get(`/rfqs/${rfq.rfq_id}/society-quote`).then((r) => setForm(r.data && r.data.rfq_id ? r.data : { validity: "15 days" }));
  }, [open]);
  const save = async () => {
    await api.post(`/rfqs/${rfq.rfq_id}/society-quote`, { ...form, amount: +(form.amount || 0), taxes: +(form.taxes || 0) });
    toast.success("Society quotation saved");
  };
  return (
    <div className="mt-3 border-t pt-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => window.open(`${API}/pdf/rfq/${rfq.rfq_id}/comparison`, "_blank")} data-testid={`wf-comparison-${rfq.rfq_id}`}>Comparison PDF</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(!open)} data-testid={`wf-prep-quote-${rfq.rfq_id}`}>{open ? "Hide" : "Prepare Society Quotation"}</Button>
      </div>
      {open && form && (
        <div className="grid grid-cols-2 gap-2 mt-3 bg-muted/40 rounded-lg p-3">
          <div className="col-span-2"><Label className="text-xs mb-1 block">Scope</Label><Textarea rows={2} value={form.scope || ""} onChange={(e) => setForm({ ...form, scope: e.target.value })} /></div>
          <div><Label className="text-xs mb-1 block">Amount (₹)</Label><Input type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`sq-amount-${rfq.rfq_id}`} /></div>
          <div><Label className="text-xs mb-1 block">Taxes (₹)</Label><Input type="number" value={form.taxes || ""} onChange={(e) => setForm({ ...form, taxes: e.target.value })} /></div>
          <div><Label className="text-xs mb-1 block">Timeline</Label><Input value={form.timeline || ""} onChange={(e) => setForm({ ...form, timeline: e.target.value })} /></div>
          <div><Label className="text-xs mb-1 block">Warranty</Label><Input value={form.warranty || ""} onChange={(e) => setForm({ ...form, warranty: e.target.value })} /></div>
          <div className="col-span-2 flex gap-2">
            <Button size="sm" onClick={save} className="rounded-full" data-testid={`sq-save-${rfq.rfq_id}`}>Save Quotation</Button>
            <Button size="sm" variant="outline" onClick={() => window.open(`${API}/pdf/society-quote/${rfq.rfq_id}`, "_blank")} className="rounded-full" data-testid={`sq-pdf-${rfq.rfq_id}`}>Quotation PDF</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Inspection({ rid, onChanged }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({});
  const load = () => api.get(`/requirements/${rid}/inspections`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [rid]);
  const save = async () => {
    if (!form.observations) { toast.error("Add observations"); return; }
    await api.post(`/requirements/${rid}/inspections`, form); toast.success("Inspection recorded"); setForm({}); load(); onChanged?.();
  };
  return (
    <div className="space-y-4">
      <div className="bg-card border rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-sm mb-1 block">Date</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><Label className="text-sm mb-1 block">Inspector</Label><Input value={form.inspector || ""} onChange={(e) => setForm({ ...form, inspector: e.target.value })} /></div>
        </div>
        <div><Label className="text-sm mb-1 block">Measurements</Label><Input value={form.measurements || ""} onChange={(e) => setForm({ ...form, measurements: e.target.value })} /></div>
        <div><Label className="text-sm mb-1 block">Observations</Label><Textarea data-testid="wf-insp-obs" value={form.observations || ""} onChange={(e) => setForm({ ...form, observations: e.target.value })} /></div>
        <div><Label className="text-sm mb-1 block">Recommendations</Label><Textarea value={form.recommendations || ""} onChange={(e) => setForm({ ...form, recommendations: e.target.value })} /></div>
        <Button onClick={save} className="rounded-full gap-2" data-testid="wf-insp-save"><Plus className="w-4 h-4" /> Record Inspection</Button>
      </div>
      {list.map((i) => (
        <div key={i.inspection_id} className="bg-muted/50 rounded-lg p-3 text-sm">
          <div className="font-medium">{i.date} · {i.inspector}</div>
          <div className="text-muted-foreground mt-1">{i.observations}</div>
          {i.recommendations && <div className="text-secondary mt-1">→ {i.recommendations}</div>}
          <PhotoAttachments entityType="inspection" entityId={i.inspection_id} label="Inspection Photos" />
        </div>
      ))}
    </div>
  );
}

function BOQ({ rid, onChanged }) {
  const [items, setItems] = useState([{ description: "", unit: "", qty: "", rate: "" }]);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    api.get(`/requirements/${rid}/boq`).then((r) => {
      if (r.data.items?.length) { setItems(r.data.items); setTotal(r.data.total); }
    });
  }, [rid]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const save = async () => {
    const clean = items.filter((it) => it.description);
    const r = await api.put(`/requirements/${rid}/boq`, { items: clean });
    setItems(r.data.items); setTotal(r.data.total); toast.success("BOQ saved"); onChanged?.();
  };
  const computed = items.reduce((s, it) => s + (+it.qty || 0) * (+it.rate || 0), 0);
  return (
    <div className="bg-card border rounded-xl p-4">
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input className="flex-1" placeholder="Description" value={it.description} onChange={(e) => upd(i, "description", e.target.value)} data-testid={`wf-boq-desc-${i}`} />
            <Input className="w-16" placeholder="Unit" value={it.unit} onChange={(e) => upd(i, "unit", e.target.value)} />
            <Input className="w-20" type="number" placeholder="Qty" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} />
            <Input className="w-24" type="number" placeholder="Rate" value={it.rate} onChange={(e) => upd(i, "rate", e.target.value)} />
            {items.length > 1 && <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></Button>}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <Button variant="outline" size="sm" onClick={() => setItems([...items, { description: "", unit: "", qty: "", rate: "" }])} className="gap-1.5"><Plus className="w-3.5 h-3.5" /> Add item</Button>
        <div className="font-semibold">Total: {inr(computed || total)}</div>
      </div>
      <Button onClick={save} className="rounded-full mt-3 w-full" data-testid="wf-boq-save">Save BOQ</Button>
    </div>
  );
}

function RFQ({ requirement, onChanged }) {
  const rid = requirement.requirement_id;
  const [vendors, setVendors] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [invited, setInvited] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [qForm, setQForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState({});

  const load = async () => {
    const [v, r] = await Promise.all([api.get("/vendors"), api.get("/rfqs")]);
    setVendors(v.data);
    const mine = r.data.filter((x) => x.requirement_id === rid);
    setRfqs(mine);
    for (const rfq of mine) {
      const qr = await api.get(`/rfqs/${rfq.rfq_id}/quotes`);
      setQuotes((prev) => ({ ...prev, [rfq.rfq_id]: qr.data.quotes }));
    }
  };
  useEffect(() => { load(); }, [rid]);

  const createRfq = async () => {
    if (invited.length === 0) { toast.error("Invite at least one vendor"); return; }
    setBusy(true);
    try { await api.post("/rfqs", { requirement_id: rid, invited_vendor_ids: invited }); toast.success("RFQ created & vendors invited"); setInvited([]); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const recordQuote = async (rfqId) => {
    const f = qForm[rfqId] || {};
    if (!f.vendor_id || !f.price) { toast.error("Select vendor and enter price"); return; }
    const v = vendors.find((x) => x.vendor_id === f.vendor_id);
    await api.post(`/rfqs/${rfqId}/quotes`, { ...f, vendor_name: v?.business_name });
    toast.success("Quote recorded"); setQForm({ ...qForm, [rfqId]: {} }); await load(); onChanged?.();
  };
  const award = async (rfqId, quoteId) => {
    if (!window.confirm("Award this quote? A Work Order & Project will be created.")) return;
    await api.post(`/rfqs/${rfqId}/award`, { quote_id: quoteId }); toast.success("Awarded — Work Order & Project created"); await load(); onChanged?.();
  };
  const toggleInvite = (id) => setInvited(invited.includes(id) ? invited.filter((x) => x !== id) : [...invited, id]);

  const loadAnalysis = async (rfqId) => {
    const r = await api.get(`/rfqs/${rfqId}/analysis`);
    setAnalysis((prev) => ({ ...prev, [rfqId]: r.data }));
  };
  const requestBafo = async (rfqId) => {
    const a = analysis[rfqId];
    const ids = (a?.quotes || []).filter((q) => q.rank && q.vendor_id).map((q) => q.vendor_id);
    if (ids.length === 0) { toast.error("No shortlist-able vendors (need vendor accounts)"); return; }
    const deadline = window.prompt("BAFO deadline (YYYY-MM-DD)?", "");
    await api.post(`/rfqs/${rfqId}/bafo/request`, { vendor_ids: ids, deadline: deadline || "" });
    toast.success(`Best & Final Offer requested from ${ids.length} vendor(s)`); await load(); onChanged?.();
  };

  return (
    <div className="space-y-4">
      {rfqs.length === 0 && (
        <div className="bg-card border rounded-xl p-4">
          <Label className="text-sm mb-2 block">Invite verified vendors to quote</Label>
          <div className="flex flex-wrap gap-2">
            {vendors.length === 0 ? <p className="text-sm text-muted-foreground">No verified vendors available yet.</p> :
              vendors.map((v) => (
                <button key={v.vendor_id} onClick={() => toggleInvite(v.vendor_id)} data-testid={`wf-invite-${v.vendor_id}`}
                  className={`text-sm border rounded-full px-3 py-1.5 ${invited.includes(v.vendor_id) ? "bg-primary text-white border-primary" : "hover:bg-muted"}`}>
                  {v.business_name}
                </button>
              ))}
          </div>
          <Button onClick={createRfq} disabled={busy} className="rounded-full mt-3 gap-2" data-testid="wf-create-rfq">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Create RFQ
          </Button>
        </div>
      )}

      {rfqs.map((rfq) => (
        <div key={rfq.rfq_id} className="bg-card border rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div><div className="font-medium">{rfq.ref} · {rfq.title}</div>{rfq.rfq_number && <div className="text-xs text-muted-foreground font-mono" data-testid={`wf-rfqno-${rfq.rfq_id}`}>{rfq.rfq_number}</div>}</div>
            <span className="text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5">{rfq.status}</span>
          </div>
          <div className="mt-3 space-y-2">
            {(quotes[rfq.rfq_id] || []).map((q) => (
              <div key={q.quote_id} className="flex items-center justify-between border rounded-lg p-2.5 text-sm">
                <div><div className="font-medium">{q.vendor_name}</div><div className="text-xs text-muted-foreground">{q.warranty} · {q.timeline}</div></div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">{inr(q.price)}</span>
                  {rfq.status !== "Awarded" ? <Button size="sm" onClick={() => award(rfq.rfq_id, q.quote_id)} className="rounded-full gap-1 h-8" data-testid={`wf-award-${q.quote_id}`}><Award className="w-3.5 h-3.5" /> Award</Button>
                    : <span className={`text-xs rounded-full px-2 py-0.5 ${q.status === "Approved" ? "bg-secondary/10 text-secondary" : "bg-muted"}`}>{q.status}</span>}
                </div>
              </div>
            ))}
            {(quotes[rfq.rfq_id] || []).length === 0 && <p className="text-xs text-muted-foreground">No quotes yet. Record quotes received from invited vendors below.</p>}
          </div>
          {(quotes[rfq.rfq_id] || []).length > 0 && (
            <div className="mt-3 border-t pt-3">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => loadAnalysis(rfq.rfq_id)} data-testid={`wf-analysis-${rfq.rfq_id}`}>Analyse (L1/L2/L3)</Button>
                {analysis[rfq.rfq_id] && rfq.status !== "Awarded" && <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => requestBafo(rfq.rfq_id)} data-testid={`wf-bafo-${rfq.rfq_id}`}>Request Best &amp; Final Offer</Button>}
              </div>
              {analysis[rfq.rfq_id] && (
                <div className="mt-2 space-y-1.5" data-testid={`wf-analysis-result-${rfq.rfq_id}`}>
                  {analysis[rfq.rfq_id].quotes.map((q) => (
                    <div key={q.quote_id} className="flex items-center justify-between text-sm border rounded-lg px-2.5 py-1.5">
                      <span className="flex items-center gap-2">
                        {q.rank && <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${q.rank === "L1" ? "bg-secondary text-white" : "bg-muted"}`}>{q.rank}</span>}
                        {q.vendor_name}
                        {q.vendor_rating ? <span className="text-xs text-amber-600">★{q.vendor_rating}</span> : null}
                        {q.final_price ? <span className="text-xs text-secondary">BAFO</span> : null}
                      </span>
                      <span className="font-semibold">{inr(q.effective_price)}</span>
                    </div>
                  ))}
                  <p className="text-[11px] text-muted-foreground">{analysis[rfq.rfq_id].note}</p>
                </div>
              )}
            </div>
          )}
          {(quotes[rfq.rfq_id] || []).length > 0 && <SocietyQuote rfq={rfq} />}
          {rfq.status !== "Awarded" && (
            <div className="mt-3 border-t pt-3 grid grid-cols-2 gap-2">
              <select className="border rounded-md h-9 px-2 text-sm col-span-2" value={(qForm[rfq.rfq_id] || {}).vendor_id || ""}
                onChange={(e) => setQForm({ ...qForm, [rfq.rfq_id]: { ...(qForm[rfq.rfq_id] || {}), vendor_id: e.target.value } })} data-testid={`wf-quote-vendor-${rfq.rfq_id}`}>
                <option value="">Select vendor…</option>
                {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.business_name}</option>)}
              </select>
              <Input type="number" placeholder="Price" value={(qForm[rfq.rfq_id] || {}).price || ""} onChange={(e) => setQForm({ ...qForm, [rfq.rfq_id]: { ...(qForm[rfq.rfq_id] || {}), price: e.target.value } })} data-testid={`wf-quote-price-${rfq.rfq_id}`} />
              <Input placeholder="Warranty" value={(qForm[rfq.rfq_id] || {}).warranty || ""} onChange={(e) => setQForm({ ...qForm, [rfq.rfq_id]: { ...(qForm[rfq.rfq_id] || {}), warranty: e.target.value } })} />
              <Button onClick={() => recordQuote(rfq.rfq_id)} variant="outline" className="rounded-full col-span-2 gap-2" data-testid={`wf-record-quote-${rfq.rfq_id}`}><Plus className="w-4 h-4" /> Record Quote</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
